// src/utils/AxiosInstance.jsx
import axios from "axios";
import Cookies from "js-cookie";
import { clearAuth } from "./authStorage";

const rawBaseURL = process.env.REACT_APP_API_URL;
const baseURL = typeof rawBaseURL === "string" ? rawBaseURL.replace(/\/+$/, "") : undefined;

let apiOrigin = null;
try {
  if (baseURL && /^https?:\/\//i.test(baseURL)) {
    apiOrigin = new URL(baseURL).origin;
  }
} catch {
  apiOrigin = null;
}

const axiosInstance = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 30000,
  headers: {
    Accept: "application/json",
  },
});

function isAbsoluteHttpUrl(url) {
  return typeof url === "string" && /^https?:\/\//i.test(url);
}

function getOrigin(url) {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

function isApiRequest(url) {
  if (!url) return true;

  return (
    !isAbsoluteHttpUrl(url) ||
    (apiOrigin && typeof url === "string" && getOrigin(url) === apiOrigin)
  );
}

function normalizedUrl(url) {
  return String(url || "");
}

function isCsrfExemptRequest(url) {
  const requestUrl = normalizedUrl(url);

  return (
    requestUrl.includes("/security/auth/login") ||
    requestUrl.includes("/site/user/new") ||
    requestUrl.includes("/site/user/confirm-create") ||
    requestUrl.includes("/site/user/forgot-password") ||
    requestUrl.includes("/site/user/confirm-code-change-password") ||
    requestUrl.includes("/site/user/change-password")
  );
}

function isAuthProbeRequest(url) {
  const requestUrl = normalizedUrl(url);
  return requestUrl.includes("/site/user/logged-user");
}

function isLoginRequest(url) {
  const requestUrl = normalizedUrl(url);
  return requestUrl.includes("/security/auth/login");
}

function notifyLoggedOut() {
  window.dispatchEvent(
    new CustomEvent("auth:changed", {
      detail: { authenticated: false, source: "axios-unauthorized" },
    })
  );
}

axiosInstance.interceptors.request.use((config) => {
  const method = (config.method || "get").toUpperCase();

  // ----------------------------------------------------------------
  // Cache-busting for GETs.
  //
  // In local dev the CRA dev server / API send "no-store" headers so
  // GETs are never replayed from cache. In production (S3-served SPA
  // hitting a separate backend, potentially with a CDN in front) the
  // browser HTTP cache is free to replay GET responses. The symptom
  // we hit: after generating plays, the optimistic credit update
  // (e.g. 191 → 190) is immediately overwritten by a stale cached
  // response from GET /site/user/logged-user that still says 191,
  // so the on-screen balance never appears to change.
  //
  // Adding a per-request `_ts` query param makes every GET URL
  // unique, so neither the browser cache nor any URL-keyed CDN
  // cache (e.g. CloudFront) can serve a hit. The backend ignores
  // unknown params, so this is safe and zero-config.
  //
  // We deliberately do this BEFORE the early-return below so that
  // GET / HEAD / OPTIONS all get the cache-buster, then short-
  // circuit past the CSRF logic (which only applies to mutations).
  // ----------------------------------------------------------------
  if (method === "GET" && isApiRequest(config?.url)) {
    config.params = { ...(config.params || {}), _ts: Date.now() };
  }

  if (["GET", "HEAD", "OPTIONS"].includes(method)) {
    return config;
  }

  if (!isApiRequest(config?.url)) {
    return config;
  }

  if (isCsrfExemptRequest(config?.url)) {
    return config;
  }

  const csrf = Cookies.get("csrf_token");

  if (csrf) {
    config.headers = config.headers || {};
    config.headers["X-CSRF-Token"] = csrf;
  }

  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const requestUrl = error?.config?.url;

    const shouldBroadcastLogout =
      isApiRequest(requestUrl) &&
      !isLoginRequest(requestUrl) &&
      !isAuthProbeRequest(requestUrl) &&
      status === 401;

    if (shouldBroadcastLogout) {
      clearAuth();
      notifyLoggedOut();
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
